import { useUpdateCachedTaskStatus } from 'modules/onboarding/setup-hub/pages/SetupHubPage/hooks/useUpdateCachedTaskStatus';
import { useMutation } from 'src/core/api/hooks/useMutation';

import { type HubTask } from '../../../types/hubTask';

type Payload = {
  endpointParams: {
    taskId: string;
  };
};

export const useMarkHubTaskAsDoneMutation = () => {
  const updateCachedTaskStatus = useUpdateCachedTaskStatus();
  return useMutation<Payload, HubTask, HubTask>({
    request: {
      type: 'rest',
      target: 'companyAPI',
      endpoint: ({ taskId }) => `/onboarding-hub/tasks/${taskId}/complete`,
      method: 'post',
    },
    reshapeData: (data) => data,
    options: {
      throwOnError: true,
      onSuccess: ({ client, rawData }) => {
        const { id: taskId, category } = rawData;
        const context = category === 'onboarding' ? 'onboarding' : 'setup_hub';

        const hubTasks = client.getQueryData<HubTask[]>([
          'onboarding-hub',
          'tasks',
          context,
        ]);

        // Should not happen. But if it does, we invalidate the cache to refetch the data
        if (!hubTasks) {
          client.invalidateQueries(['onboarding-hub', 'tasks', context]);
          return;
        }

        const updatedHubTask = hubTasks.find((task) => task.id === taskId);

        // Should not happen either. But if it does, we invalidate the cache to refetch the data
        if (!updatedHubTask) {
          client.invalidateQueries(['onboarding-hub', 'tasks', context]);
          return;
        }

        client.setQueryData<HubTask[]>(
          ['onboarding-hub', 'tasks', context],
          (tasks) => {
            if (!tasks) {
              return [];
            }
            return tasks.map((task) => {
              if (task.id === taskId) {
                return { ...task, status: 'completed' };
              }
              return task;
            });
          },
        );

        updateCachedTaskStatus(rawData.type, context);
      },
    },
  });
};
